<template>
    <div class="automation-wizard-field-date-time">
        <AutomationWizardFieldDate
            :field="dayField"
            :modelValue="dayValue"
            @update:model-value="handleDayUpdate"
        />
        <AutomationWizardFieldTime
            :field="timeField"
            :modelValue="timeValue"
            @update:model-value="handleTimeUpdate"
        />
    </div>
</template>

<script>
import AutomationWizardFieldDate from './AutomationWizardFieldDate.vue';
import AutomationWizardFieldTime from './AutomationWizardFieldTime.vue';

export default {
    name: 'AutomationWizardFieldDateTime',
    components: {
        AutomationWizardFieldDate,
        AutomationWizardFieldTime,
    },
    props: {
        field: {
            type: Object,
            required: true,
        },
        modelValue: {
            type: [String, Number, Object],
            default: '',
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            dayValue: '',
            timeValue: '',
        };
    },
    computed: {
        dayField() {
            const inputProps = this.field.props?.input;
            if (inputProps) {
                return {
                    ...this.field,
                    props: {
                        input: { value: inputProps.value || '' },
                        formItem: inputProps.formItem || {},
                    },
                    required: this.field.required,
                };
            }
            return {
                ...this.field,
                props: {
                    input: { value: '' },
                    formItem: this.field.formItem || {},
                },
                required: this.field.required,
            };
        },
        timeField() {
            const timePickerProps = this.field.props?.timePicker;
            if (timePickerProps) {
                return {
                    ...this.field,
                    props: {
                        timePicker: { value: timePickerProps.value || '' },
                        formItem: timePickerProps.formItem || {},
                    },
                    required: this.field.required,
                };
            }
            return {
                ...this.field,
                props: {
                    timePicker: { value: '' },
                    formItem: this.field.formItem || {},
                },
                required: this.field.required,
            };
        },
    },
    watch: {
        modelValue: {
            handler(newValue) {
                this.parseValue(newValue);
            },
            immediate: true,
        },
        dayValue() {
            this.emitCombinedValue();
        },
        timeValue() {
            this.emitCombinedValue();
        },
    },
    mounted() {
        this.parseValue(this.modelValue);
    },
    methods: {
        parseValue(value) {
            const defaultDayValue = this.field.props?.input?.value || '';
            const defaultTimeValue = this.field.props?.timePicker?.value || '';
            if (value && typeof value === 'string') {
                this.dayValue = this.extractValuePart(value, 'day') || defaultDayValue;
                this.timeValue = this.extractValuePart(value, 'hour') || defaultTimeValue;

                const minutes = this.extractValuePart(value, 'minute');
                if (minutes !== '') {
                    this.timeValue = this.timeValue + ' ' + minutes;
                }
            } else {
                this.dayValue = defaultDayValue;
                this.timeValue = defaultTimeValue;
            }
        },
        extractValuePart(value, unit) {
            const match = value.match(new RegExp(`\\+\\s*(\\d+(?:\\.\\d+)?)\\s*${unit}`));
            return match ? match[0] : '';
        },
        handleDayUpdate(newDayValue) {
            this.dayValue = newDayValue;
            this.$nextTick(() => {
                this.emitCombinedValue();
            });
        },
        handleTimeUpdate(newTimeValue) {
            this.timeValue = newTimeValue;
            this.$nextTick(() => {
                this.emitCombinedValue();
            });
        },
        emitCombinedValue() {
            const combinedValue = `${this.dayValue} ${this.timeValue}`.trim();
            this.$emit('update:modelValue', combinedValue);
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-field-date-time {
    display: flex;
    gap: var(--spacing-3);
}
</style>
